import { Paper, Text, Group, Badge, Stack, Box } from '@mantine/core'
import { IconShield, IconAlertTriangle } from '@tabler/icons-react'
import { MarkdownRenderer } from '../common/MarkdownRenderer'

const profileIcons = {
  mobilizer: '⚡',
  stabilizer: '🛡️',
  connector: '🔗',
  strategist: '🎯',
  fixer: '🔧',
}

/**
 * Displays the player's response profile.
 * Supports both legacy shape (description/strength/risk) and
 * new story debrief shape (observation narrative from AI).
 */
export function ResponseProfileCard({ profile, observation }) {
  if (!profile) return null

  // Support both old {primary, description, strength, risk} and
  // new {primary_tendency} from session_summary
  const primary = profile.primary || profile.primary_tendency
  if (!primary) return null

  const { description, strength, risk } = profile

  return (
    <Paper
      p="lg"
      className="glass"
      style={{
        borderColor: 'rgba(0, 229, 255, 0.2)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Subtle background pattern */}
      <Box
        style={{
          position: 'absolute',
          top: -20,
          right: -20,
          fontSize: 80,
          opacity: 0.04,
          lineHeight: 1,
          pointerEvents: 'none',
        }}
      >
        {profileIcons[primary] || '🎯'}
      </Box>

      <Stack gap="md">
        <Group gap="sm">
          <Text size="xl" style={{ lineHeight: 1 }}>
            {profileIcons[primary] || '🎯'}
          </Text>
          <Box>
            <Text
              size="xs"
              c="dimmed"
              ff="Rajdhani, sans-serif"
              fw={600}
              tt="uppercase"
              lts={1}
            >
              Primary Tendency
            </Text>
            <Text
              size="lg"
              fw={700}
              ff="Orbitron, sans-serif"
              c="cyan.3"
              tt="capitalize"
              className="glow-text-cyan"
            >
              {primary}
            </Text>
          </Box>
        </Group>

        {/* Legacy mode: description + strength/risk badges */}
        {description && (
          <Text size="sm" c="gray.4" lh={1.5}>
            {description}
          </Text>
        )}

        {(strength || risk) && (
          <Group gap="sm">
            {strength && (
              <Badge
                size="md"
                variant="light"
                color="green"
                leftSection={<IconShield size={12} />}
              >
                {strength}
              </Badge>
            )}
            {risk && (
              <Badge
                size="md"
                variant="light"
                color="orange"
                leftSection={<IconAlertTriangle size={12} />}
              >
                {risk}
              </Badge>
            )}
          </Group>
        )}

        {/* New story debrief mode: rich AI observation */}
        {observation && (
          <MarkdownRenderer content={observation} />
        )}
      </Stack>
    </Paper>
  )
}
